import {TFunction} from 'i18next';
import _, { uniq } from 'lodash';
import { Store } from 'redux';

import { ContentLocalesI, localeToLanguage, systemLocales } from '~src/interfaces/session.types';
import { StoreI } from '~src/rootReducer';

import logger from './logger/LlsLogger';
import {
  getBrowserPreferredSystemLocale,
  getCompanyContentLocale,
  getContentLocale,
} from './session';

let contentTranslationLocales: string[] = [];

export function subscribeContentLanguageToStore(store: Store) {
  store.subscribe(() => {
    const state = store.getState() as StoreI;
    contentTranslationLocales = uniq([
      getContentLocale(state, false),
      getBrowserPreferredSystemLocale(),
      getCompanyContentLocale(state),
    ].filter((s) => Boolean(s)));
  });
}

// To get the key in which the content is translated
export function getTranslateKey(translationObject: ContentLocalesI) {
  if (!translationObject) {
    logger.warn('getTranslateKey called without translationObject');
    return null;
  }

  // get the first match of the selected locales in the upper order
  for (const locale of contentTranslationLocales) {
    if (translationObject[locale]) {
      return locale;
    }
  }

  const nonEmptyTranslations = _.pickBy(translationObject);
  const firstKey = Object.keys(nonEmptyTranslations)[0];
  return firstKey || null;
}

export function translateContent(translationObject: ContentLocalesI) {
  if (!translationObject) {
    logger.warn('translateContent called without translationObject');
    return '';
  }

  for (const locale of contentTranslationLocales) {
    if (translationObject[locale]) {
      return translationObject[locale];
    }
  }

  const nonEmptyTranslations = _.pickBy(translationObject);
  const firstNonEmptyTranslation = translationObject[Object.keys(nonEmptyTranslations)[0]];

  return firstNonEmptyTranslation || '';
}

export function translateError(t: TFunction, error: string, data?: Record<string, string>) {
  return t(`x.error.${error}`, data);
}

export function getSystemTranslations(t: TFunction, key: string): ContentLocalesI {
  const result: ContentLocalesI = {};
  systemLocales.forEach((systemLocale) => {
    const languageKey = localeToLanguage(systemLocale);

    const translation = t(key, { lngs: [systemLocale, languageKey] });
    if (translation || translation === '') {
      result[systemLocale] = translation;
    }
  });

  return result;
}

export function addContentTranslation(
  newTranslation: string,
  existingTranslations: ContentLocalesI = {},
): ContentLocalesI {
  const locale = contentTranslationLocales[0];
  return {
    ...existingTranslations,
    [locale]: newTranslation,
  };
}
